import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { identify } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { changeLanguage } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      if (res.data?.user_id) identify(res.data.user_id, { role: res.data.role });
      // Golden rule: the backend's preferred_language ALWAYS wins over the
      // local selector on any device once the user is authenticated.
      if (res.data?.preferred_language) changeLanguage(res.data.preferred_language, { persist: false });
    } catch {
      localStorage.removeItem("auth_token");
    } finally {
      setLoading(false);
    }
  }, [changeLanguage]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (token, userData) => {
    localStorage.setItem("auth_token", token);
    setUser(userData);
    if (userData?.user_id) identify(userData.user_id, { role: userData.role });
    if (userData?.preferred_language) changeLanguage(userData.preferred_language, { persist: false });
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    setUser(null);
  };

  // Merges a partial update (e.g. a new/removed avatar) into the current user without
  // a full /auth/me round-trip — keeps Navbar and every other consumer in sync instantly.
  const updateUser = (patch) => setUser((u) => (u ? { ...u, ...patch } : u));

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
